"use client";

import { useState } from "react";

import type { ChordPlacement, ChordQuality } from "@/lib/virtuosa-layout/types";

const ROOT_NAMES = [
  "C",
  "C♯",
  "D",
  "D♯",
  "E",
  "F",
  "F♯",
  "G",
  "G♯",
  "A",
  "A♯",
  "B",
] as const;

// Major and minor are 78 % of the catalogue's chords and the only two whose
// shape the tutorial teaches; the other seven were shown at the same visual
// weight, which made nine equal-looking options out of a two-option decision.
const PRIMARY_QUALITIES: { id: ChordQuality; label: string }[] = [
  { id: "maj", label: "majeur" },
  { id: "min", label: "mineur" },
];

const EXTRA_QUALITIES: { id: ChordQuality; label: string }[] = [
  { id: "7", label: "7" },
  { id: "maj7", label: "maj7" },
  { id: "m7", label: "m7" },
  { id: "dim", label: "dim" },
  { id: "aug", label: "aug" },
  { id: "m7b5", label: "m7♭5" },
  { id: "dim7", label: "dim7" },
];

interface ChordPickerProps {
  rootSemi: number;
  quality: ChordQuality;
  withBass: boolean;
  placement: ChordPlacement | null;
  onRootChange: (semi: number) => void;
  onQualityChange: (quality: ChordQuality) => void;
  onWithBassChange: (withBass: boolean) => void;
}

export function ChordPicker({
  rootSemi,
  quality,
  withBass,
  placement,
  onRootChange,
  onQualityChange,
  onWithBassChange,
}: ChordPickerProps) {
  const [showExtras, setShowExtras] = useState(false);

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1.5 text-[10px] uppercase tracking-wider text-text-muted">
          Fondamentale
        </div>
        <div className="flex flex-wrap gap-1">
          {ROOT_NAMES.map((name, i) => (
            <button
              key={name}
              type="button"
              onClick={() => onRootChange(i)}
              className={`min-w-[34px] rounded-md border px-2 py-1 font-mono text-xs transition-all duration-100 cursor-pointer ${
                rootSemi === i
                  ? "border-accent bg-accent/20 text-accent-bright"
                  : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1.5 text-[10px] uppercase tracking-wider text-text-muted">
          Qualité
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {PRIMARY_QUALITIES.map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => onQualityChange(q.id)}
              className={`rounded-md border px-3 py-1 text-xs transition-all duration-100 cursor-pointer ${
                quality === q.id
                  ? "border-accent bg-accent/20 text-accent-bright"
                  : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
              }`}
            >
              {q.label}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setShowExtras((v) => !v)}
            aria-expanded={showExtras}
            className="ml-1 rounded-md px-2 py-1 text-[11px] text-text-muted hover:text-text-secondary cursor-pointer"
          >
            {showExtras ? "moins" : "plus…"}
          </button>

          {showExtras
            ? EXTRA_QUALITIES.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => onQualityChange(q.id)}
                  className={`rounded-md border px-2.5 py-1 font-mono text-xs transition-all duration-100 cursor-pointer ${
                    quality === q.id
                      ? "border-accent bg-accent/20 text-accent-bright"
                      : "border-border-subtle bg-bg-elevated/60 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                  }`}
                >
                  {q.label}
                </button>
              ))
            : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={withBass}
            onChange={(e) => onWithBassChange(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-accent)]"
          />
          Avec basse main gauche
        </label>

        <ChordStatus placement={placement} />
      </div>
    </div>
  );
}

// What the player needs to know is whether the chord can be played and whether
// the bass made it onto the keyboard. Stretch, octave shift and hand counts are
// lattice-optimisation figures: they were printed here as "Stretch: 1.80", read
// like a score being missed, and meant nothing to anyone learning the shape.
function ChordStatus({ placement }: { placement: ChordPlacement | null }) {
  if (!placement) {
    return (
      <span className="text-[11px] italic text-err">
        accord non plaçable
      </span>
    );
  }

  if (placement.fallback) {
    return (
      <span className="text-[11px] italic text-amber-300/80">
        basse hors du clavier — joue la main droite seule
      </span>
    );
  }

  return null;
}
